import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { listPostsByAuthor } from '@/lib/api/posts';
import type { Post, Reaction } from '@/lib/api/posts';

export type ActivityPost = { post: Post; reactions: Reaction[]; commentCount: number };

async function fetchActivityPage(authorId: string, before: string): Promise<ActivityPost[]> {
  const posts = await listPostsByAuthor(authorId, before, 20);
  if (!posts.length) return [];

  const postIds = posts.map((p) => p.id);
  const [reactionsRes, countsRes] = await Promise.all([
    supabase
      .from('reactions')
      .select('id, post_id, author_id, kind, created_at')
      .in('post_id', postIds)
      .throwOnError(),
    supabase
      .from('comments')
      .select('post_id', { count: 'exact' })
      .in('post_id', postIds)
      .throwOnError(),
  ]);

  const reactionsByPost = new Map<string, Reaction[]>();
  for (const r of reactionsRes.data ?? []) {
    const arr = reactionsByPost.get(r.post_id) ?? [];
    arr.push(r as Reaction);
    reactionsByPost.set(r.post_id, arr);
  }
  const countByPost = new Map<string, number>();
  for (const row of countsRes.data ?? []) {
    countByPost.set(row.post_id, (countByPost.get(row.post_id) ?? 0) + 1);
  }

  return posts.map((p) => ({
    post: p,
    reactions: reactionsByPost.get(p.id) ?? [],
    commentCount: countByPost.get(p.id) ?? 0,
  }));
}

export function useProfileActivity(authorId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ['profile-activity', authorId],
    queryFn: ({ pageParam }) =>
      fetchActivityPage(authorId!, (pageParam as string | null) ?? new Date().toISOString()),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage[lastPage.length - 1].post.created_at;
    },
    enabled: !!authorId,
  });
}
